import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import api from '@/api'
import { isNative } from '@/services/platform'
import { useUserStore } from '@/stores/user'
import { tokenStore } from '@/services/tokenStore'

/**
 * alarmNotifications — In-Tab Browser-Benachrichtigungen für neue Kunden-Alarme.
 *
 * Mechanik (bewusst leichtgewichtig, KEINE Service-Worker/VAPID/Backend-Push):
 *   - Ein globaler Poller fragt GET /partner-customers/events?since=<lastSeen> ab
 *     (Cross-Customer-Aggregat) und feuert pro neuem Alarm über der eingestellten
 *     Schwelle eine Web-Notification (Notification API) + einen In-App-Toast.
 *   - Läuft NUR im Browser (isNative === false — native iOS nutzt APNs via
 *     pushService.js) und nur für Partner-User mit erteilter Browser-Berechtigung.
 *
 * Einstellungen (nicht sensibel) werden in localStorage gehalten; die Berechtigung
 * selbst ist ohnehin pro Browser/Gerät — daher kein Server-Sync nötig.
 *
 * Der In-App-Toast wird über dasselbe CustomEvent 'secsu:push-notification'
 * ausgelöst, das App.vue bereits behandelt (Wiederverwendung der Push-Infra).
 */

const ALL_SEVERITIES = ['critical', 'high', 'medium', 'low']

const LS_ENABLED    = 'secsu-alarm-notify-enabled'
const LS_SEVERITIES = 'secsu-alarm-notify-severities'
const LS_LASTSEEN   = 'secsu-alarm-notify-lastseen'

const POLL_MS = 30_000
const SUMMARY_THRESHOLD = 3   // ab >3 neuen Alarmen eine Sammel-Notification

function loadSeverities() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_SEVERITIES) || 'null')
    if (raw && typeof raw === 'object') {
      return {
        critical: raw.critical !== false,   // Default: an
        high:     raw.high     !== false,    // Default: an
        medium:   raw.medium   === true,     // Default: aus
        low:      raw.low      === true,     // Default: aus
      }
    }
  } catch { /* ignore */ }
  return { critical: true, high: true, medium: false, low: false }
}

export const useAlarmNotificationsStore = defineStore('alarmNotifications', () => {
  const userStore = useUserStore()

  // ── State (persistiert) ─────────────────────────────────────────────────────
  const enabled    = ref(localStorage.getItem(LS_ENABLED) !== 'false')   // Default: an
  const severities = ref(loadSeverities())
  const lastSeenAt = ref(localStorage.getItem(LS_LASTSEEN) || null)
  const permission = ref(typeof Notification !== 'undefined' ? Notification.permission : 'denied')

  const supported = computed(() => typeof window !== 'undefined' && 'Notification' in window && !isNative)
  const active    = computed(() => supported.value && enabled.value && permission.value === 'granted')

  let timer = null
  let routerRef = null
  let polling = false
  // Übersetzer wird von App.vue/Settings injiziert (läuft außerhalb Component-Context).
  // Die t-Funktion liest activeLang reaktiv — einmaliges Capturen reicht.
  let tFn = (key) => key
  function setTranslator(t) { if (typeof t === 'function') tFn = t }

  // ── Persistenz ──────────────────────────────────────────────────────────────
  watch(enabled,    (v) => localStorage.setItem(LS_ENABLED, String(v)))
  watch(severities, (v) => localStorage.setItem(LS_SEVERITIES, JSON.stringify(v)), { deep: true })
  watch(lastSeenAt, (v) => { if (v) localStorage.setItem(LS_LASTSEEN, v) })

  function severityAllowed(sev) {
    const key = (sev || '').toLowerCase()
    return ALL_SEVERITIES.includes(key) && severities.value[key] === true
  }

  // ── Berechtigung ────────────────────────────────────────────────────────────
  async function requestPermission() {
    if (!supported.value) return permission.value
    try {
      permission.value = await Notification.requestPermission()
    } catch { /* ignore */ }
    return permission.value
  }

  // ── Eine Notification (+ In-App-Toast) abfeuern ─────────────────────────────
  function notify({ title, body, link, tag }) {
    // Browser-Notification (auch bei nicht-fokussiertem Tab)
    if (active.value) {
      try {
        const n = new Notification(title, { body, tag, icon: '/logo.png' })
        n.onclick = () => {
          try { window.focus() } catch { /* ignore */ }
          if (link && routerRef) routerRef.push(link).catch(() => {})
          n.close()
        }
      } catch { /* ignore */ }
    }
    // In-App-Toast über die bestehende Push-Infra (App.vue hört darauf)
    try {
      window.dispatchEvent(new CustomEvent('secsu:push-notification', {
        detail: { title, body, data: { route: link } },
      }))
    } catch { /* ignore */ }
  }

  function sendTest() {
    notify({
      title: 'secDASH',
      body:  tFn('partnerCustomers.notifTestBody'),
      tag:   'secsu-alarm-test',
    })
  }

  // ── Poll-Tick ───────────────────────────────────────────────────────────────
  async function tick() {
    if (polling) return
    if (!active.value || !userStore.isPartner || !tokenStore.getSync()) return
    polling = true
    try {
      const params = { limit: 50 }
      if (lastSeenAt.value) params.since = lastSeenAt.value
      const { data } = await api.getPartnerCustomerEvents(params)
      const events = Array.isArray(data?.data) ? data.data : []
      if (!events.length) return

      // lastSeen über ALLE neuen Events hinweg vorrücken (auch unter der Schwelle),
      // damit nicht jeder Tick dieselben Events erneut zieht.
      const newest = events.reduce(
        (m, e) => (e.received_at && e.received_at > m ? e.received_at : m),
        lastSeenAt.value || ''
      )

      const matching = events.filter(e => severityAllowed(e.parsed?.severity))

      if (matching.length > SUMMARY_THRESHOLD) {
        notify({
          title: tFn('partnerCustomers.notifSummaryTitle', { count: matching.length }),
          body:  tFn('partnerCustomers.notifSummaryBody'),
          link:  '/partner/customers/list',
          tag:   'secsu-alarms-summary',
        })
      } else {
        for (const e of matching) {
          const sev = (e.parsed?.severity || '').toUpperCase()
          notify({
            title: e.parsed?.title || e.parsed?.alertName || tFn('partnerCustomers.notifNewAlarm'),
            body:  [e.customer_name, sev].filter(Boolean).join(' · '),
            link:  `/partner/customers/${e.customer_id}`,
            tag:   `secsu-alarm-${e.id}`,
          })
        }
      }

      if (newest) lastSeenAt.value = newest
    } catch { /* still poll next interval */ } finally {
      polling = false
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────
  function start(router = null, t = null) {
    if (router) routerRef = router
    if (t) setTranslator(t)
    if (!supported.value) return
    // Erst-Lauf: ab jetzt benachrichtigen, keine Historie nachholen.
    if (!lastSeenAt.value) lastSeenAt.value = new Date().toISOString()
    if (timer) return
    timer = setInterval(tick, POLL_MS)
    tick()
  }

  function stop() {
    if (timer) { clearInterval(timer); timer = null }
  }

  return {
    enabled, severities, lastSeenAt, permission,
    supported, active,
    requestPermission, sendTest, start, stop, setTranslator,
  }
})
